import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { COUNTRIES, DEFAULT_COUNTRY } from "@/lib/countries";
import { Loader2, ShieldCheck, ChevronLeft, ChevronDown } from "lucide-react";

const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return e?.message || "Something went wrong";
};

const roleTarget = (role) =>
  role === "customer" ? "/app" :
  role === "restaurant_owner" ? "/restaurant" :
  role === "rider" ? "/rider" :
  role === "provider" ? "/provider" : "/admin";

export const PhoneLoginPanel = () => {
  const { setSession } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isAr = lang === "ar";

  const [step, setStep] = useState("phone");
  const [dialCode, setDialCode] = useState(DEFAULT_COUNTRY.dial);
  const [phone, setPhone] = useState("");
  const [sentPhone, setSentPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const send = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (phone.replace(/\D/g, "").length < 6) return setErr(isAr ? "أدخل رقم هاتف صحيح" : "Please enter a valid phone number.");
    const localNum = phone.replace(/\D/g, "").replace(/^0+/, "");
    const full = `${dialCode}${localNum}`;
    setSentPhone(full);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/phone/login/send-otp", { phone: full });
      setStep("otp");
      if (data.dev_code) setDevCode(data.dev_code);
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) return setErr(isAr ? "أدخل الرمز المكون من 6 أرقام" : "Enter the 6-digit code.");
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/phone/login/verify-otp", { phone: sentPhone, code });
      setSession(data.token, data.user);
      navigate(location.state?.from?.pathname || roleTarget(data.user.role), { replace: true });
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally { setLoading(false); }
  };

  if (step === "otp") {
    return (
      <div data-testid="phone-login-otp">
        <button type="button" onClick={() => { setStep("phone"); setOtp(""); setErr(""); }}
          className="mb-3 inline-flex items-center gap-1 text-sm text-[var(--so-text-soft)] hover:text-[var(--so-orange)]" data-testid="phone-login-back">
          <ChevronLeft className="w-4 h-4" /> {isAr ? "رجوع" : "Back"}
        </button>
        <p className="text-sm text-[var(--so-text-soft)]">{isAr ? "أدخل الرمز المرسل إلى" : "Enter the code sent to"} <span dir="ltr" className="font-semibold">{sentPhone}</span></p>
        {devCode && (
          <div className="mt-3 text-xs rounded-lg p-2.5 bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400" data-testid="phone-login-devcode">
            {isAr ? "وضع تجريبي: رمزك هو" : "TEST MODE — your code is"} <b className="tracking-widest">{devCode}</b>
          </div>
        )}
        <form onSubmit={verify} className="mt-4">
          <input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} dir="ltr"
            value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="••••••" className="so-input text-center text-2xl tracking-[0.5em] font-bold" data-testid="phone-login-otp-input" />
          {err && <div className="mt-3 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="phone-login-error">{err}</div>}
          <button type="submit" disabled={loading} className="so-btn-primary w-full justify-center mt-4" data-testid="phone-login-verify">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4" /> {isAr ? "تحقق وادخل" : "Verify & sign in"}</>}
          </button>
          <button type="button" onClick={send} disabled={loading} className="w-full mt-2 text-sm text-[var(--so-text-soft)] hover:text-[var(--so-orange)]" data-testid="phone-login-resend">
            {isAr ? "إعادة إرسال الرمز" : "Resend code"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <form onSubmit={send} data-testid="phone-login-form">
      <label className="text-xs font-semibold uppercase tracking-wider text-[var(--so-text-soft)]">{isAr ? "رقم الهاتف" : "Phone number"}</label>
      <div className="flex gap-2 mt-1" dir="ltr">
        <div className="relative flex-shrink-0" style={{ width: "7.5rem" }}>
          <select value={dialCode} onChange={(e) => setDialCode(e.target.value)}
            className="so-input appearance-none cursor-pointer w-full" style={{ width: "100%", paddingInlineStart: "0.7rem", paddingInlineEnd: "1.6rem" }}
            data-testid="phone-login-country">
            {COUNTRIES.map((c) => <option key={c.iso} value={c.dial}>{c.flag} {c.dial}</option>)}
          </select>
          <ChevronDown className="w-4 h-4 absolute top-1/2 -translate-y-1/2 end-2 pointer-events-none text-[var(--so-text-soft)]" />
        </div>
        <input className="so-input flex-1 min-w-0" style={{ width: "auto" }} type="tel" dir="ltr" placeholder="9xx xxx xxx"
          value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="phone-login-phone" />
      </div>
      {err && <div className="mt-4 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="phone-login-error">{err}</div>}
      <button type="submit" disabled={loading} className="so-btn-primary w-full justify-center mt-6" data-testid="phone-login-send">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isAr ? "أرسل رمز الدخول" : "Send login code")}
      </button>
    </form>
  );
};
